import { isNil } from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  QueryKey,
  UseInfiniteQueryOptions,
  hashQueryKey,
  useInfiniteQuery,
} from "react-query";

export type UsePaginationQueryCallerArgs = {
  page: number;
  offset: number;
};

export type UsePaginationQueryParams<T> = {
  /**
   * The default page size of the call.
   */
  pageSize?: number | null;
  key: QueryKey;
  fetcher: (args: UsePaginationQueryCallerArgs) => Promise<T>;
  enabled?: boolean;
} & UseInfiniteQueryOptions<T>;

export type UsePaginationQueryReturn<T> = {
  data: T;
  /**
   * Resets the query from the cache, and performs a refetch, updating the
   * isRefetching flag.
   */
  reset: () => void;
  /**
   * Indicates whether the data is being loaded. Generally used to detect when
   * data is being loaded. Useful to know if this is initial loading of data.
   */
  isLoading: boolean;
  /**
   * Indicate if the query is being refetched by called the reset function.
   */
  isRefetching: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
};

export function usePaginationQuery<T>({
  pageSize,
  fetcher,
  enabled,
  key,
  ...rest
}: UsePaginationQueryParams<T>): UsePaginationQueryReturn<T> {
  const [page, setPage] = useState(0);
  const hashedQueryKey = hashQueryKey(key);

  useEffect(() => {
    setPage(0);
  }, [hashedQueryKey]);

  const {
    data,
    isLoading,
    remove,
    fetchNextPage,
    isFetchingNextPage,
    isRefetching,
    ...queryProps
  } = useInfiniteQuery<T>({
    ...rest,
    queryKey: key,
    queryFn: ({ pageParam }) =>
      fetcher({
        page: pageParam,
        offset: isNil(pageParam) ? 0 : (pageParam + 1) * (pageSize ?? 40),
      }),
    getNextPageParam: () => page,
    enabled,
    refetchOnMount: false,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const loadMore = useCallback(() => {
    setPage((page) => page + 1);
    fetchNextPage();
  }, [fetchNextPage]);

  const reset = useCallback(() => {
    setPage(0);
    remove();
  }, [remove]);

  return {
    data: data?.pages?.flat(1) as T,
    reset,
    isLoading,
    isRefetching: isRefetching && !isFetchingNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore,
    ...queryProps,
  };
}
